/**
 * The swarm pulse (amendment 14).
 *
 * Touching cells with something going on are drawn as one swarm, with a faint line between
 * every two that touch, and every few seconds a pulse runs out from the hottest of them, cell by
 * cell, brighter with every cell it passes. It is a picture of numbers the map already shows and
 * changes none of them; which cells form a swarm, and in what order the pulse reaches them, is
 * decided in `$domain/swarm.ts`.
 *
 * On a tilted map (amendment 15) the cells stand as columns (`cell-columns.ts`), the lines
 * become arcs between their tops and the ring runs round each column's foot. The columns do
 * their own bouncing and glowing; the timing both use is `cell-motion.ts`.
 *
 * A WebGL layer of its own, because both obvious alternatives fail:
 *
 *   * MapLibre layers with animated paint. A member's brightness depends on the time *and* on
 *     its own distance from the origin, so the value would differ per feature and change every
 *     frame — and MapLibre answers every change to a data-driven paint value by tiling the
 *     source again.
 *   * A canvas over the map. It would cover the street names, which the hexagons sit below on
 *     purpose (brief §14), and it could not hide behind a building.
 *
 * Here the geometry goes to the GPU once per data change, and a frame costs a few uniforms.
 *
 * And one heartbeat for every swarm on screen rather than one each, because MapLibre draws the
 * whole map for an animation frame, tiles and labels and all. With every swarm pulsing at once,
 * the map is still for most of each period and draws nothing at all in between — which on a
 * phone is the difference between a pulse and a warm pocket. This layer is the one that asks
 * for those frames; the columns draw in the same ones.
 */

import { boundaryOf, type Cell, centreOf } from '$domain/h3.ts';
import { type Swarm, swarmsOf } from '$domain/swarm.ts';
import type { CustomLayerInterface, Map as MapLibreMap } from 'maplibre-gl';
import {
  columnMetres,
  FLASH,
  FULL_STRENGTH,
  GLSL_MOTION,
  heartbeat,
  liftFor,
  MAX_HOP,
  PERIOD,
  STEP,
} from './cell-motion.ts';
import { gdiRgb } from './gdi-scale.ts';
import type { CellCollection } from './types.ts';
import {
  anchored,
  buildProgram,
  glsl,
  mercator,
  type Point,
  vertexArray,
} from './webgl.ts';

/** At most one frame per this many milliseconds. A glow needs no 60 fps; a battery notices. */
const FRAME_MS = 33;
/**
 * Half the width of a link, in screen pixels at every zoom. In map units it would be a hair
 * in the overview and a finger's width at street level.
 */
const LINK_PX = 2.2;
/**
 * How far a member's ring travels outward, and how thick it is, in sizes of the cell. Just past
 * the edge: a ring that reached the next cell but one would cross its neighbours' rings and
 * the swarm would turn into a lattice.
 */
const RING_TRAVEL = 0.55;
const RING_BAND = 0.08;
/** How strongly a link shows when no pulse is on it: there, but quieter than the cells. */
const LINK_REST = 0.16;
/** How high a link arcs above the straight line between two column tops, in cell sizes. */
const ARC = 0.25;
/** Pieces a link is cut into, so that it can bend into that arc. */
const SEGMENTS = 10;
/** A link ends this much above a column's top, so that the two never fight over a pixel. */
const ABOVE_TOP = 1.02;

/** What a vertex belongs to. A `lit` link carries the pulse; a `still` one only joins. */
const KIND = { lit: 0, still: 1, fill: 2, ring: 3 } as const;
/**
 * Floats per vertex: base (2), offset (2), param (2), hop, colour (3), strength, kind, and for a
 * link the column tops at its two ends and the height of its arc (3), in mercator units.
 */
const STRIDE = 15;
/** MapLibre's world is 512 px wide at zoom 0. */
const WORLD_PX = 512;

const VERTEX = `#version 300 es
precision highp float;
uniform mat4 u_matrix;
uniform float u_tau;
uniform float u_link;
uniform float u_lift;
layout(location = 0) in vec2 a_base;
layout(location = 1) in vec2 a_offset;
layout(location = 2) in vec2 a_param;
layout(location = 3) in float a_hop;
layout(location = 4) in vec3 a_colour;
layout(location = 5) in float a_strength;
layout(location = 6) in float a_kind;
layout(location = 7) in vec3 a_height;
out vec2 v_param;
out vec3 v_colour;
out float v_flash;
out float v_head;
out float v_amp;
flat out int v_kind;
${GLSL_MOTION}
void main() {
  int kind = int(a_kind + 0.5);
  float since = sinceHit(u_tau, a_hop);
  float progress = clamp(since / FLASH, 0.0, 1.0);
  float amp = amplitude(a_hop, a_strength);
  vec2 position = a_base;
  float z = 0.0;
  if (kind == ${KIND.ring}) {
    position += a_offset * (1.0 + ${glsl(RING_TRAVEL)} * progress + ${glsl(RING_BAND)} * a_param.x);
  } else if (kind == ${KIND.fill}) {
    position += a_offset;
  } else {
    // A link: sideways in screen-sized steps, and on a tilted map up between the columns'
    // tops, whose ends rise with the columns when the pulse makes them bounce.
    position += a_offset * u_link;
    float along = a_param.x;
    float hopTo = kind == ${KIND.lit} ? a_hop + 1.0 : a_hop;
    float from = a_height.x * (1.0 + BOUNCE * bounceOf(u_tau, a_hop) * amp);
    float to = a_height.y * (1.0 + BOUNCE * bounceOf(u_tau, hopTo) * amplitude(hopTo, a_strength));
    z = u_lift * (mix(from, to, along) + a_height.z * sin(3.14159265 * along));
  }
  gl_Position = u_matrix * vec4(position, z, 1.0);
  v_param = a_param;
  v_colour = a_colour;
  v_kind = kind;
  v_flash = flashOf(u_tau, a_hop);
  v_head = since / STEP;
  v_amp = amp;
}`;

// The light itself is the cell's colour taken towards white on the dark map: more of the same
// saturated colour on a cell already filled with it barely shows. On the light map white is
// no light at all, so there it stays the colour.
const FRAGMENT = `#version 300 es
precision highp float;
uniform float u_dark;
uniform float u_lift;
in vec2 v_param;
in vec3 v_colour;
in float v_flash;
in float v_head;
in float v_amp;
flat in int v_kind;
out vec4 fragColour;
void main() {
  vec3 light = mix(v_colour, vec3(1.0), 0.45 * u_dark);
  vec3 colour = v_colour;
  float alpha;
  if (v_kind == ${KIND.lit} || v_kind == ${KIND.still}) {
    float across = 1.0 - abs(v_param.y);
    float glow = 0.0;
    if (v_kind == ${KIND.lit}) {
      float d = (v_param.x - v_head) / 0.16;
      float trail = v_param.x < v_head && v_head < 1.25
        ? 0.4 * smoothstep(v_head - 0.5, v_head, v_param.x)
        : 0.0;
      glow = v_amp * (exp(-d * d) + trail);
    }
    colour = mix(v_colour, light, clamp(glow, 0.0, 1.0));
    alpha = across * (${glsl(LINK_REST)} + glow);
  } else if (v_kind == ${KIND.fill}) {
    // Standing columns glow themselves; the flash on the ground fades out as they rise.
    colour = light;
    alpha = 0.4 * v_flash * v_amp * (1.0 - u_lift);
  } else {
    float edge = sin(3.14159265 * v_param.x);
    colour = light;
    alpha = 0.85 * v_flash * v_amp * edge * edge;
  }
  alpha = clamp(alpha, 0.0, 1.0);
  fragColour = vec4(colour * alpha, alpha);
}`;

type Geometry = {
  vertices: Float32Array;
  count: number;
  /** What every vertex is measured from; see `anchored`. */
  anchor: Point;
  /** Seconds from a heartbeat until the last member has stopped glowing. */
  span: number;
};

const NOTHING: Geometry = {
  vertices: new Float32Array(0),
  count: 0,
  anchor: { x: 0, y: 0 },
  span: 0,
};

const ORIGIN: Point = { x: 0, y: 0 };

/** Everything the GPU needs to draw these swarms, built once per change of the data. */
function geometryOf(swarms: readonly Swarm[]): Geometry {
  const first = swarms[0];
  if (first === undefined) return NOTHING;

  const anchor = mercator(centreOf(first.origin));
  const local = (position: { lat: number; lon: number }): Point => {
    const point = mercator(position);
    return { x: point.x - anchor.x, y: point.y - anchor.y };
  };

  const data: number[] = [];
  let furthest = 0;
  const vertex = (
    base: Point,
    offset: Point,
    param: [number, number],
    hop: number,
    colour: [number, number, number],
    strength: number,
    kind: number,
    height: [number, number, number] = [0, 0, 0],
  ) => {
    data.push(
      base.x,
      base.y,
      offset.x,
      offset.y,
      ...param,
      hop,
      ...colour,
      strength,
      kind,
      ...height,
    );
  };

  for (const swarm of swarms) {
    const strength = Math.min(1, swarm.total / FULL_STRENGTH);
    const members = new Map<
      Cell,
      { centre: Point; hop: number; colour: [number, number, number]; top: number; size: number }
    >();

    for (const member of swarm.members) {
      const hop = Math.min(member.hop, MAX_HOP);
      furthest = Math.max(furthest, hop);
      const position = centreOf(member.cell);
      const centre = local(position);
      const colour = gdiRgb(member.gdi);
      // `boundaryOf` closes the ring, so its last point is its first again.
      const corners = boundaryOf(member.cell)
        .slice(0, -1)
        .map(([lon, lat]) => {
          const corner = local({ lat, lon });
          return { x: corner.x - centre.x, y: corner.y - centre.y };
        });

      corners.forEach((corner, index) => {
        const next = corners[(index + 1) % corners.length] ?? corner;
        // The flash, a fan over the cell.
        vertex(centre, ORIGIN, [0, 0], hop, colour, strength, KIND.fill);
        vertex(centre, corner, [0, 0], hop, colour, strength, KIND.fill);
        vertex(centre, next, [0, 0], hop, colour, strength, KIND.fill);
        // The ring, a band whose inner (0) and outer (1) edge the shader moves outward.
        vertex(centre, corner, [0, 0], hop, colour, strength, KIND.ring);
        vertex(centre, next, [0, 0], hop, colour, strength, KIND.ring);
        vertex(centre, corner, [1, 0], hop, colour, strength, KIND.ring);
        vertex(centre, next, [0, 0], hop, colour, strength, KIND.ring);
        vertex(centre, next, [1, 0], hop, colour, strength, KIND.ring);
        vertex(centre, corner, [1, 0], hop, colour, strength, KIND.ring);
      });

      const corner = corners[0] ?? ORIGIN;
      members.set(member.cell, {
        centre,
        hop,
        colour,
        // The column's top, as `cell-columns.ts` stands it, in mercator units.
        top: columnMetres(member.cell, member.gdi) * mercator(position).metre * ABOVE_TOP,
        size: Math.hypot(corner.x, corner.y),
      });
    }

    for (const link of swarm.links) {
      const from = members.get(link.from);
      const to = members.get(link.to);
      if (from === undefined || to === undefined) continue;
      const dx = to.centre.x - from.centre.x;
      const dy = to.centre.y - from.centre.y;
      const length = Math.hypot(dx, dy);
      if (length === 0) continue;
      // A unit normal; the shader scales it to `LINK_PX` for the zoom of the moment.
      const normal = { x: -dy / length, y: dx / length };
      const kind = link.carries ? KIND.lit : KIND.still;
      const height: [number, number, number] = [from.top, to.top, ARC * from.size];
      const corner = (along: number, side: number) =>
        vertex(
          { x: from.centre.x + dx * along, y: from.centre.y + dy * along },
          { x: normal.x * side, y: normal.y * side },
          [along, side],
          from.hop,
          from.colour,
          strength,
          kind,
          height,
        );
      for (let piece = 0; piece < SEGMENTS; piece += 1) {
        const a = piece / SEGMENTS;
        const b = (piece + 1) / SEGMENTS;
        corner(a, 1);
        corner(a, -1);
        corner(b, 1);
        corner(a, -1);
        corner(b, -1);
        corner(b, 1);
      }
    }
  }

  return {
    vertices: new Float32Array(data),
    count: data.length / STRIDE,
    anchor,
    // The last member lights up `furthest` steps after the heartbeat and glows for FLASH; the
    // light on a link fades a quarter of a step after it arrives.
    span: furthest * STEP + Math.max(FLASH, 1.25 * STEP),
  };
}

export type SwarmPulse = {
  /** For `addLayer`: above the hexagons, below the labels like them. */
  layer: CustomLayerInterface;
  setCells(collection: CellCollection | undefined): void;
  /** Off while the map shows something other than the hexagons. */
  setEnabled(enabled: boolean): void;
  destroy(): void;
};

export function createSwarmPulse({ id, dark }: { id: string; dark: boolean }): SwarmPulse {
  let map: MapLibreMap | undefined;
  let gpu:
    | {
        program: WebGLProgram;
        vao: WebGLVertexArrayObject;
        buffer: WebGLBuffer;
        uniform: (name: string) => WebGLUniformLocation | null;
      }
    | undefined;
  let broken = false;
  let geometry = NOTHING;
  let uploaded = false;
  let enabled = true;
  let frame: number | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastFrame = 0;

  // "Reduce motion" keeps the swarm on the map, links and all, and moves nothing.
  const motion =
    typeof matchMedia === 'function' ? matchMedia('(prefers-reduced-motion: reduce)') : undefined;
  const still = () => motion?.matches === true;
  const running = () =>
    enabled &&
    map !== undefined &&
    geometry.count > 0 &&
    !still() &&
    document.visibilityState === 'visible';

  function stop() {
    if (frame !== undefined) cancelAnimationFrame(frame);
    if (timer !== undefined) clearTimeout(timer);
    frame = undefined;
    timer = undefined;
  }

  function schedule() {
    if (frame !== undefined || timer !== undefined || !running()) return;
    frame = requestAnimationFrame(tick);
  }

  function tick(now: number) {
    frame = undefined;
    if (!running()) return;
    const tau = heartbeat(now);
    if (tau < geometry.span) {
      if (now - lastFrame >= FRAME_MS) {
        lastFrame = now;
        map?.triggerRepaint();
      }
      frame = requestAnimationFrame(tick);
      return;
    }
    // This pulse is over: one frame to clear its last glow, then nothing until the next.
    map?.triggerRepaint();
    timer = setTimeout(
      () => {
        timer = undefined;
        schedule();
      },
      (PERIOD - tau) * 1000,
    );
  }

  const onMotion = () => {
    if (!still()) return schedule();
    stop();
    map?.triggerRepaint();
  };
  const onVisibility = () => (document.visibilityState === 'visible' ? schedule() : stop());
  motion?.addEventListener('change', onMotion);
  document.addEventListener('visibilitychange', onVisibility);

  /**
   * GL resources, made on the first frame rather than in `onAdd`.
   *
   * Two reasons. MapLibre resets its own GL state after `render` and not after `onAdd`, so
   * binding anything there leaves its bookkeeping out of step with the context. And `onAdd`
   * runs inside `addLayer`: a throw there would end the map's `load` handler half-way, without
   * numbers, flags or taps. A pulse that cannot be drawn is no reason to lose the map, so a
   * failure here is logged once and the layer stays empty.
   */
  function prepare(gl: WebGL2RenderingContext) {
    if (gpu !== undefined || broken) return gpu;
    try {
      const program = buildProgram(gl, VERTEX, FRAGMENT);
      const buffer = gl.createBuffer();
      if (buffer === null) throw new Error('no buffer');
      // location, components, offset — in floats, matching STRIDE.
      const vao = vertexArray(
        gl,
        buffer,
        [
          [0, 2, 0],
          [1, 2, 2],
          [2, 2, 4],
          [3, 1, 6],
          [4, 3, 7],
          [5, 1, 10],
          [6, 1, 11],
          [7, 3, 12],
        ],
        STRIDE,
      );
      const uniforms = new Map<string, WebGLUniformLocation | null>();
      gpu = {
        program,
        vao,
        buffer,
        uniform: (name) => {
          if (!uniforms.has(name)) uniforms.set(name, gl.getUniformLocation(program, name));
          return uniforms.get(name) ?? null;
        },
      };
      uploaded = false;
    } catch (error) {
      broken = true;
      console.error('swarm pulse disabled', error);
    }
    return gpu;
  }

  const layer: CustomLayerInterface = {
    id,
    type: 'custom',
    // 3D, so that a building or a column in front hides the pulse behind it.
    renderingMode: '3d',
    onAdd(added) {
      map = added;
      schedule();
    },
    onRemove(_removed, gl) {
      stop();
      if (gpu !== undefined) {
        gl.deleteProgram(gpu.program);
        gl.deleteVertexArray(gpu.vao);
        gl.deleteBuffer(gpu.buffer);
      }
      gpu = undefined;
      map = undefined;
    },
    render(gl, options) {
      if (map === undefined || geometry.count === 0) return;
      // Mercator only. Under a globe the matrix means something else, and drawing nothing is
      // better than drawing a swarm where there is none.
      const projection = map.getProjection()?.type;
      if (projection !== undefined && projection !== 'mercator') return;
      const ready = prepare(gl);
      if (ready === undefined) return;

      gl.bindVertexArray(ready.vao);
      if (!uploaded) {
        gl.bindBuffer(gl.ARRAY_BUFFER, ready.buffer);
        gl.bufferData(gl.ARRAY_BUFFER, geometry.vertices, gl.STATIC_DRAW);
        uploaded = true;
      }
      gl.useProgram(ready.program);
      gl.uniformMatrix4fv(
        ready.uniform('u_matrix'),
        false,
        anchored(options.defaultProjectionData.mainMatrix, geometry.anchor),
      );
      // With motion reduced the clock stands far before any pulse: the links are drawn, and
      // nothing glows.
      gl.uniform1f(ready.uniform('u_tau'), still() ? -1e4 : heartbeat(performance.now()));
      gl.uniform1f(ready.uniform('u_link'), LINK_PX / (WORLD_PX * 2 ** map.getZoom()));
      gl.uniform1f(ready.uniform('u_lift'), liftFor(map.getPitch()));
      gl.uniform1f(ready.uniform('u_dark'), dark ? 1 : 0);
      // Tested against what stands in front, written to nothing: light has no depth.
      gl.enable(gl.DEPTH_TEST);
      gl.depthFunc(gl.LEQUAL);
      gl.depthMask(false);
      gl.enable(gl.BLEND);
      // Premultiplied. On the dark map the light adds up, which is what makes it glow; on the
      // light one adding makes white, so there it is laid over instead.
      gl.blendFunc(gl.ONE, dark ? gl.ONE : gl.ONE_MINUS_SRC_ALPHA);
      gl.drawArrays(gl.TRIANGLES, 0, geometry.count);
      gl.bindVertexArray(null);
    },
  };

  return {
    layer,
    setCells(collection) {
      const cells = (collection?.features ?? []).map(({ properties }) => ({
        cell: properties.cell,
        gdi: properties.gdi,
      }));
      geometry = geometryOf(swarmsOf(cells));
      uploaded = false;
      map?.triggerRepaint();
      schedule();
    },
    setEnabled(value) {
      enabled = value;
      if (value) schedule();
      else stop();
    },
    destroy() {
      stop();
      motion?.removeEventListener('change', onMotion);
      document.removeEventListener('visibilitychange', onVisibility);
      map = undefined;
    },
  };
}
